// Intro phase — the "Bắt đầu" landing screen of the new flow.
// docs/fe-state-machine.md (new flow). Replaces the old Boot welcome.
//
// Visuals (all from FE-static /textures): cloud sky backdrop (bg_indexA),
// the stage (bg_index), the hero art (flow/introContent.png), the header logos
// (flow/LogoHeader.png), and the start button (flow/buttonStart.png).
//
// On enter: resolve uid from ?uid, fire POST /v1/check (so a returning player's
// stored neutral head can stand in for the default character later). On "Bắt
// đầu" tap (the user gesture): unlock audio, then transition to ChooseCup.
// Camera + face model are NOT requested here — they load lazily only if the
// player chooses to capture (custom flow).
//
// Positions are authored as % of the 9:16 frame and grouped below for tuning —
// they're approximate vs the Figma and meant to be dialled in on-device.

import type { Phase } from "../state/types";
import { check, MaintenanceError } from "../api/client";
import { mountFrame, clearChildren } from "../ui/overlay";
import { initAudioBus } from "../audio";

const FLOW = "/textures/flow";

// --- Layout tunables (% of the 9:16 frame; dial on-device vs Figma) ---
// introContent holds the title + the ambassador. Offset it DOWN so the
// character's feet land in the middle of the stage oval (part of bg_index).
const CONTENT_TOP = "13%";
const CONTENT_WIDTH = "92%";
const CONTENT_MAX_H = "80%";
// Header logos sit in the empty sky band at the very top. This is Intro's OWN
// logo position — other scenes place LogoHeader differently (top of their flex
// column), and they don't need to match.
const LOGO_TOP = "2%";
const LOGO_WIDTH = "86%";

// A player identity must be a single URL/path-safe token. In production it's the
// iCheck UUID (?user_id=) or the dev ?uid= fallback — both fit [A-Za-z0-9._-]. A
// space (or "/", etc.) survives into the per-user asset URLs the BE returns
// (/v1/assets/<uid>/...) and the unquoted CSS url() that renders the composed
// heads in Review, silently breaking the flow there. So we reject it here, at the
// only point identity enters the app, instead of three screens later.
const UID_SAFE = /^[A-Za-z0-9._-]+$/;
const isValidUid = (uid: string): boolean => UID_SAFE.test(uid);

let cleanup: (() => void) | null = null;

export const Intro: Phase = {
  async enter(ctx, transition) {
    const root = document.getElementById("app")!;
    clearChildren(root);

    // Identity: the iCheck embed passes a stable anonymous UUID as ?user_id=;
    // ?uid= is kept as a standalone/dev fallback. ?winner_id= is the play-record
    // id iCheck needs back for the win report + close_game (null when standalone).
    // docs/integration-icheck.md#inbound--iframe-url-params.
    const params = new URLSearchParams(location.search);
    const rawIdentity = (params.get("user_id") ?? params.get("uid") ?? "").trim();
    const fromUrl = isValidUid(rawIdentity) ? rawIdentity : null;
    // Present-but-malformed (e.g. a typed space) vs simply absent — drives the
    // message below so a bad uid doesn't masquerade as a missing one.
    const identityInvalid = rawIdentity.length > 0 && fromUrl === null;
    const winnerRaw = params.get("winner_id");
    const winnerId = winnerRaw && /^\d+$/.test(winnerRaw) ? Number(winnerRaw) : null;
    const token = params.get("token") ?? null;

    // Cloud-sky backdrop behind the 9:16 frame.
    const bg = document.createElement("div");
    bg.className = "app-bg";
    bg.style.backgroundImage = "url(/textures/bg_indexA.png)";
    bg.style.backgroundColor = "#f7c7d8";

    const frame = document.createElement("div");
    frame.className = "app-frame";
    frame.style.position = "relative";
    frame.style.overflow = "hidden";

    // Layered visuals (DOM order = stacking, later is on top): stage at the
    // bottom, hero art over it, header logos ON TOP of the hero (so they show in
    // the top sky band rather than being hidden behind introContent), start
    // button anchored near the bottom.
    frame.innerHTML = `
      <img src="/textures/bg_index.png" alt="" style="
        position:absolute;bottom:0;left:50%;transform:translateX(-50%);
        width:100%;object-fit:contain;pointer-events:none;"/>
      <img src="${FLOW}/introContent.png" alt="" style="
        position:absolute;top:${CONTENT_TOP};left:50%;transform:translateX(-50%);
        width:${CONTENT_WIDTH};max-height:${CONTENT_MAX_H};object-fit:contain;pointer-events:none;"/>
      <img src="${FLOW}/LogoHeader.png" alt="" style="
        position:absolute;top:${LOGO_TOP};left:50%;transform:translateX(-50%);
        width:${LOGO_WIDTH};object-fit:contain;pointer-events:none;"/>
      <button id="start" style="
        position:absolute;bottom:5%;left:50%;transform:translateX(-50%);
        width:55%;background:none;border:0;padding:0;cursor:pointer;
        background-image:url(${FLOW}/buttonStart.png);background-size:contain;
        background-repeat:no-repeat;background-position:center;aspect-ratio:612/208;
        opacity:0;transition:opacity 150ms;" aria-label="Bắt đầu"></button>
      <div id="loading" style="
        position:absolute;bottom:6%;left:50%;transform:translateX(-50%);
        font-size:14px;color:#7a2a3a;font-weight:bold;display:none;"></div>
    `;

    root.appendChild(bg);
    const frameCleanup = mountFrame(root, frame);
    cleanup = () => {
      if (bg.parentElement === root) root.removeChild(bg);
      frameCleanup();
    };

    const startBtn = frame.querySelector<HTMLButtonElement>("#start")!;
    const loading = frame.querySelector<HTMLElement>("#loading")!;

    // No valid uid → can't proceed. Minimal inline setter for testing (no dev-bar
    // yet). `identityInvalid` = a uid WAS supplied but isn't path-safe (e.g. has a
    // space) — distinct from one simply being absent.
    if (!fromUrl) {
      loading.style.display = "block";
      loading.innerHTML = `
        <div style="text-align:center;">${
          identityInvalid
            ? "Mã định danh không hợp lệ — chỉ cho phép chữ, số và . _ - (không khoảng trắng)"
            : "Thiếu mã định danh (?user_id= hoặc ?uid=)"
        }</div>
        <div style="display:flex;gap:6px;margin-top:8px;justify-content:center;">
          <input id="uidin" placeholder="test1" style="padding:4px 8px;border-radius:6px;border:1px solid #c98;width:120px;"/>
          <button id="uidgo" style="padding:4px 12px;border-radius:6px;border:0;background:#e34;color:#fff;font-weight:bold;cursor:pointer;">Mở</button>
        </div>
        <div id="uiderr" style="display:none;text-align:center;color:#b00;font-size:11px;margin-top:5px;">Không được chứa khoảng trắng hoặc ký tự đặc biệt</div>`;
      const go = () => {
        const v = (frame.querySelector<HTMLInputElement>("#uidin")!).value.trim();
        if (!isValidUid(v)) {
          frame.querySelector<HTMLElement>("#uiderr")!.style.display = "block";
          return;
        }
        const next = new URLSearchParams(location.search);
        next.set("uid", v);
        location.search = next.toString();
      };
      frame.querySelector<HTMLButtonElement>("#uidgo")!.addEventListener("click", go);
      frame.querySelector<HTMLInputElement>("#uidin")!.addEventListener("keydown", (e) => {
        if (e.key === "Enter") go();
      });
      return;
    }
    ctx.uid = fromUrl;
    ctx.winnerId = winnerId;
    ctx.token = token;

    // Fire /v1/check — verify eligibility và load assets
    // Block start button cho đến khi check xong
    startBtn.style.opacity = "0.3";
    startBtn.style.pointerEvents = "none";
    loading.style.display = "block";
    loading.textContent = "Đang kiểm tra...";

    try {
      const res = await check(fromUrl, winnerId ?? undefined, token ?? undefined);
      ctx.isRegistered = res.isRegistered;
      if (res.isRegistered) {
        ctx.assets.character = res.assets;
        if (typeof res.isMale === "boolean") ctx.isMale = res.isMale;
      }
      loading.style.display = "none";
      startBtn.style.opacity = "1";
      startBtn.style.pointerEvents = "auto";
    } catch (err) {
      if (err instanceof MaintenanceError) {
        transition("Maintenance");
        return;
      }
      loading.style.display = "none";
      // Hiện lỗi, không cho bấm start
      const errDiv = document.createElement("div");
      errDiv.style.cssText = "position:absolute;inset:0;z-index:10;display:flex;align-items:self-start;justify-content:center;padding:24px;";
      errDiv.innerHTML = `<div style="background:#fff;border-radius:14px;padding:24px;max-width:300px;text-align:center;font-size:15px;color:#b00;font-weight:bold;line-height:1.5;">Lượt chơi không hợp lệ</div>`;
      frame.appendChild(errDiv);
      return;
    }

    startBtn.addEventListener("click", async () => {
      startBtn.style.pointerEvents = "none";
      startBtn.style.opacity = "0.6";
      try {
        await initAudioBus();
      } catch (err) {
        console.error("[Intro] audio init failed", err);
      }
      transition("ChooseCup");
    });
  },
  exit() {
    cleanup?.();
    cleanup = null;
  },
};
